import React, {FC, memo, useState} from "react";
import {Button, Space, Tag, Tooltip, Typography} from "antd";
import {LeftOutlined, RightOutlined} from "@ant-design/icons";
import {TextBlockPreviewState} from "./useTextBlockPreview";

const {Text} = Typography;

const barStyle: React.CSSProperties = {padding: "6px 15px", borderBottom: "1px solid #f0f0f0", fontSize: "0.85em", display: "flex", flexDirection: "column", gap: 4};

/**
 * Empfängerleiste der Vorschau (Vor/Zurück, „3 von 80", Name, E-Mail, Betreff) und Legende
 * „12 ersetzt · 2 ohne Inhalt · 1 unbekannt" mit aufklappbaren Namen (Plan 2026-09-02 § 3.2).
 * Ohne Empfänger-IDs bleibt nur der Hinweis, dass Variablen beim Versand ersetzt werden.
 */
const ResolvedPreviewBar: FC<{preview: TextBlockPreviewState}> = ({preview}) => {
    const [showNames, setShowNames] = useState(false);
    const [showSummaryNames, setShowSummaryNames] = useState(false);
    const {possible, loading, error, recipients, current, summary, sample, index, setIndex, showVariableNames, setShowVariableNames} = preview;

    if (!possible) {
        return (
            <div style={barStyle}>
                <Text type="secondary">{"Variablen werden beim Versand ersetzt."}</Text>
            </div>
        );
    }

    if (error) {
        return (
            <div style={barStyle}>
                <Text type="danger">{`Vorschau konnte nicht geladen werden: ${error.message}`}</Text>
            </div>
        );
    }

    if (!current) {
        return (
            <div style={barStyle}>
                <Text type="secondary">{loading ? "Vorschau wird geladen…" : "Keine Empfänger gefunden."}</Text>
            </div>
        );
    }

    const recipient = recipients[index];
    const total = recipients.length;
    const names = (keys: Array<string>, color?: string) => keys.map((key) => (
        <Tag key={key} color={color} style={{marginRight: 2}}>
            {key}
        </Tag>
    ));

    return (
        <div style={barStyle} data-testid="resolved-preview-bar">
            <Space size="small" wrap>
                {total > 1 && (
                    <Space size={2}>
                        <Button size="small" type="text" icon={<LeftOutlined />} onClick={() => setIndex(index - 1)} disabled={index === 0 || loading} aria-label="Voriger Empfänger" />
                        <Text strong>{`${index + 1} von ${total}`}</Text>
                        <Button size="small" type="text" icon={<RightOutlined />} onClick={() => setIndex(index + 1)} disabled={index >= total - 1 || loading} aria-label="Nächster Empfänger" />
                    </Space>
                )}
                {sample ? (
                    <Text type="secondary">{"Musterdaten — beim Versand stehen hier die echten Werte"}</Text>
                ) : (
                    <>
                        <Text strong>{recipient?.name}</Text>
                        {recipient?.email && <Text type="secondary">{recipient.email}</Text>}
                    </>
                )}
                <Text type="secondary">{"Betreff:"}</Text>
                <Text>{current.subject}</Text>
            </Space>
            <Space size="small" wrap>
                <Tooltip title="Zählt für den angezeigten Empfänger. Leere Werte verschwinden lautlos wie in der Mail, unbekannte Variablen bleiben roh.">
                    <Text type="secondary">{`${current.replaced.length} ersetzt · ${current.empty.length} ohne Inhalt · `}</Text>
                </Tooltip>
                <Text type={current.unknown.length > 0 ? "danger" : "secondary"}>{`${current.unknown.length} unbekannt`}</Text>
                {current.replaced.length + current.empty.length + current.unknown.length > 0 && (
                    <Button size="small" type="link" style={{padding: 0, height: "auto", fontSize: "inherit"}} onClick={() => setShowNames(!showNames)}>
                        {showNames ? "Namen ausblenden" : "Namen anzeigen"}
                    </Button>
                )}
                <Text type="secondary">{"·"}</Text>
                <Tooltip title="Zeigt in der Vorschau hinter jedem gelb markierten Wert, aus welcher Variable er stammt.">
                    <Button size="small" type="link" style={{padding: 0, height: "auto", fontSize: "inherit"}} onClick={() => setShowVariableNames(!showVariableNames)}>
                        {showVariableNames ? "Variablennamen ausblenden" : "Variablennamen einblenden"}
                    </Button>
                </Tooltip>
            </Space>
            {showNames && (
                // Auch ersetzte Namen: der Betreff bleibt ohne Markup, dort greift der Schalter nicht.
                <div>
                    {names(current.replaced, "gold")}
                    {names(current.empty)}
                    {names(current.unknown, "red")}
                </div>
            )}
            {summary && summary.total > 1 && (
                <Space size="small" wrap data-testid="resolved-preview-summary">
                    <Text type="secondary">{summary.recipientsWithEmpty === 0 ? `Bei keinem der ${summary.total} Empfänger fehlen Inhalte` : `Bei ${summary.recipientsWithEmpty} von ${summary.total} Empfängern fehlen Inhalte`}</Text>
                    {summary.recipientsWithUnknown > 0 && <Text type="danger">{`· bei ${summary.recipientsWithUnknown} unbekannte Variablen`}</Text>}
                    {summary.emptyVariables.length > 0 && (
                        <Button size="small" type="link" style={{padding: 0, height: "auto", fontSize: "inherit"}} onClick={() => setShowSummaryNames(!showSummaryNames)}>
                            {showSummaryNames ? "Namen ausblenden" : "Namen anzeigen"}
                        </Button>
                    )}
                    {showSummaryNames && names(summary.emptyVariables)}
                </Space>
            )}
        </div>
    );
};

export default memo(ResolvedPreviewBar);
